import { Typography } from "antd";
import React, { Suspense } from "react";
import type { ActionNodeData } from "../nodes/ActionNode";
import { ACTION_FORM_REGISTRY, CUSTOM_VIEW_ALLOWLIST, type StepFormProps } from "../registry/actionFormRegistry";
import type { NormalizedStepConfig } from "../types/workflowSharedTypes";
import { DynamicStepForm } from "./DynamicStepForm";

const { Text } = Typography;

const UnknownViewFallback: React.FC = () => (
    <div className="px-1 py-3">
        <Text className="hs-fs-12 hs-color-mediumDark">
            This action requires a newer version of the app to configure.
        </Text>
    </div>
);

const ConfigNotAvailableFallback: React.FC = () => (
    <div className="px-1 py-3">
        <Text className="hs-fs-12 hs-color-mediumDark">Configuration for this action type is not yet available.</Text>
    </div>
);

export interface StepFormResolverProps {
    actionCode: string;
    stepConfig?: NormalizedStepConfig;
    data: ActionNodeData;
    update: (partial: Partial<ActionNodeData>) => void;
    nodeId: string;
    /** Draft-only apply-once (Automated Response); parent commits on Save. */
    applyOnceDraft?: boolean;
    onApplyOnceDraftChange?: (value: boolean) => void;
    onStepFormBusyChange?: (busy: boolean) => void;
}

export const StepFormResolver: React.FC<StepFormResolverProps> = ({
    actionCode,
    stepConfig,
    data,
    update,
    nodeId,
    applyOnceDraft,
    onApplyOnceDraftChange,
    onStepFormBusyChange,
}) => {
    const registryEntry = ACTION_FORM_REGISTRY[actionCode];

    if (stepConfig?.customViewKey) {
        const CustomComponent = CUSTOM_VIEW_ALLOWLIST[stepConfig.customViewKey];
        if (CustomComponent) {
            const extra = stepConfig.customViewParams ?? {};
            return (
                <Suspense
                    fallback={
                        <div className="d-flex align-items-center justify-content-center">
                            <div className="actionIconsSprite dropDownLoading position-absolute" />
                        </div>
                    }
                >
                    <CustomComponent
                        key={nodeId || "action-draft"}
                        data={data}
                        update={update}
                        nodeId={nodeId}
                        applyOnceDraft={applyOnceDraft}
                        onApplyOnceDraftChange={onApplyOnceDraftChange}
                        onStepFormBusyChange={onStepFormBusyChange}
                        {...extra}
                    />
                </Suspense>
            );
        }
        return <UnknownViewFallback />;
    }

    if (stepConfig?.formFields?.length) {
        return <DynamicStepForm fields={stepConfig.formFields} data={data} update={update} />;
    }

    if (registryEntry?.formFields?.length) {
        return <DynamicStepForm fields={registryEntry.formFields} data={data} update={update} />;
    }

    if (registryEntry?.customComponent) {
        const Component = registryEntry.customComponent;
        const props: StepFormProps & Record<string, unknown> = {
            data,
            update,
            nodeId,
            applyOnceDraft,
            onApplyOnceDraftChange,
            onStepFormBusyChange,
        };
        return <Component key={nodeId || "action-draft"} {...props} />;
    }

    return <ConfigNotAvailableFallback />;
};
